
import React, { useState } from 'react';

import { translate } from '../../../../base/i18n';
import {
    Icon,
    IconCalendar,
    IconCopy,
    IconEmail,
    IconGoogle,
    IconOutlook,
    IconWhatsapp,
    IconYahoo
} from '../../../../base/icons';
import { copyText, openURLInBrowser } from '../../../../base/util';

type Props = {

  /**
   * The encoded invitation subject.
   */
  inviteSubject: string,

  /**
   * The encoded invitation text to be sent.
   */
  inviteText: string,

  /**
   * Boolean for whether the password is set or not.
   */
  isPassword: boolean,

  /**
   * Invoked to obtain translated strings.
   */
  t: Function,
};

/**
 * Component that renders email invite options.
 *
 * @returns {React$Element<any>}
 */
function InviteByEmailSection({
    inviteSubject,
    inviteText,
    isPassword,
    t
}: Props) {
    const [ isActive, setIsActive ] = useState(true);
    const encodedInviteSubject = encodeURIComponent(inviteSubject);
    const encodedInviteText = encodeURIComponent(inviteText);

    /**
   * Copies the conference invitation to the clipboard.
   *
   * @returns {void}
   */
    function _onCopyText() {
        copyText(inviteText);
    }

    /**
   * Opens an email provider containing the conference invite.
   *
   * @param {string} url - The url to be opened.
   * @returns {Function}
   */
    function _onSelectProvider(url) {
        return function() {
            openURLInBrowser(url, true);
        };
    }

    /**
   * Toggles the email invite drawer.
   *
   * @returns {void}
   */
    function _onToggleActiveState() {
        setIsActive(!isActive);
    }

    /**
   * Renders clickable elements that each open an email client
   * containing a conference invite.
   *
   * @returns {React$Element<any>}
   */
    function renderEmailIcons() {
        const PROVIDER_MAPPING = [
            {
                icon: IconCopy,
                tooltipKey: 'addPeople.copyInvite',
                onClick: _onCopyText
            },
            {
                icon: IconEmail,
                tooltipKey: 'addPeople.defaultEmail',
                url: `mailto:?subject=${encodedInviteSubject}&body=${encodedInviteText}`
            },
            {
                icon: IconWhatsapp,
                tooltipKey: 'addPeople.sendWhatsapp',
                url: `https://wa.me/?text=${encodedInviteText}`
            },
            {
                icon: IconCalendar,
                tooltipKey: 'addPeople.googleCalendar',

                url: `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodedInviteSubject}&details=${encodedInviteText}`
            },
            {
                icon: IconGoogle,
                tooltipKey: 'addPeople.googleEmail',
                url: `https://mail.google.com/mail/?view=cm&fs=1&su=${encodedInviteSubject}&body=${encodedInviteText}`
            },
            {
                icon: IconOutlook,
                tooltipKey: 'addPeople.outlookEmail',

                url: `https://outlook.office.com/mail/deeplink/compose?subject=${encodedInviteSubject}&body=${encodedInviteText}`
            },
            {
                icon: IconYahoo,
                tooltipKey: 'addPeople.yahooEmail',
                url: `https://compose.mail.yahoo.com/?To=&Subj=${encodedInviteSubject}&Body=${encodedInviteText}`
            }
        ];

        return (
            <>
                {PROVIDER_MAPPING.map(({ icon, tooltipKey, url, onClick }, idx) => (
                    <div
                        className = { tooltipKey === 'addPeople.copyInvite' ? 'email-icon copy' : 'email-icon' }
                        key = { idx }
                        onClick = { onClick ? onClick : _onSelectProvider(url) }>
                        <Icon src = { icon } />
                    </div>
                ))}
            </>
        );
    }

    return (
        <>
            <div>
                <div
                    className = { `invite-more-dialog email-container${
                        isActive ? ' active' : ''
                    }` }
                    onClick = { _onToggleActiveState }>
                    <span>
                        {isPassword
                            ? t('addPeople.shareInviteP')
                            : t('addPeople.shareInvite')}
                    </span>
                </div>
                <div
                    className = { 'invite-more-dialog icon-container active' }>
                    <div className = 'email-icons'>{renderEmailIcons()}</div>
                </div>
            </div>
            <div className = 'invite-more-dialog separator' />
        </>
    );
}

export default translate(InviteByEmailSection);
